import React, { FC, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Dialog, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CloseIcon from '@mui/icons-material/Close';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { MuiColorInput } from 'mui-color-input';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import PlanPicker from '../../pages/create-team/components/plan-picker.component';
import PlanIncludes from '../../pages/create-team/components/plan-includes.component';
import ProgramCheckoutForm from '../../pages/billing/program-checkout-form';
import useProgramBillingActions from '../program-billing/use-program-billing-actions';
import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import ProgramsService from '../../services/programs.service';
import TeamsService from '../../services/teams.service';
import { palette } from '../../theme';
import { FieldErrors, useFieldErrors } from '../form-validation';
import {
  BillingInterval,
  BillingPlanCard,
  BillingPlanKey,
  BillingPlansResponse,
  Program,
} from '../../models/billing.models';
import {
  accountBillingInterval,
  clearPendingPaidTeam,
  formatCents,
  formatIntervalTotals,
  formatMoney,
  isAcademicEmail,
  isEarlyAccessTrial,
  isProgramLocked,
  isUnlimitedTeamCreator,
  memberCapPhrase,
  savePendingPaidTeam,
  subscriptionTotals,
  trialLengthPhrase,
} from '../../helpers/billing.helpers';
import './create-team.dialog.css';

const DEFAULT_COLOR = palette.primary.main;
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

type UnlimitedTeamSummary = {
  id: number;
  name: string;
  athlete_count?: number;
};

export interface CreateTeamDialogProps {
  open: boolean;
  onClose: () => void;
  /** Plan already chosen outside the dialog. The dialog opens on team details. */
  initialPlanKey?: BillingPlanKey | null;
  initialInterval?: BillingInterval;
}

const Check: FC = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

const Arrow: FC = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h13" />
    <path d="M12.5 5.5L19 12l-6.5 6.5" />
  </svg>
);

/** Plan choice (new coaches) and team details, or "add a squad" for coaches with a program. */
const CreateTeamDialog: FC<CreateTeamDialogProps> = ({ open, onClose, initialPlanKey = null, initialInterval }) => {
  const programsService = useService(ProgramsService);
  const teamsService = useService(TeamsService);
  const { showSnackbar } = useSnackbarHelper();
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState<BillingPlanCard[]>([]);
  const [offer, setOffer] = useState<Omit<BillingPlansResponse, 'plans'> | null>(null);
  const [currentProgram, setCurrentProgram] = useState<Program | null>(null);
  const [ownedPrograms, setOwnedPrograms] = useState<Program[]>([]);
  const [unlimitedTeams, setUnlimitedTeams] = useState<UnlimitedTeamSummary[]>([]);
  const [step, setStep] = useState<'plan' | 'team' | 'payment'>('plan');
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);
  const [billNote, setBillNote] = useState('');
  const [checkoutMode, setCheckoutMode] = useState<'setup' | 'payment'>('payment');
  const [pendingSubscriptionId, setPendingSubscriptionId] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<BillingPlanKey | null>('program');
  const [intervals, setIntervals] = useState<Partial<Record<BillingPlanKey, BillingInterval>>>({
    small_team: 'month',
    program: 'month',
    academic: 'year',
  });
  const [teamName, setTeamName] = useState('');
  const [teamColor, setTeamColor] = useState<string>(DEFAULT_COLOR);
  const [teamDescription, setTeamDescription] = useState('');
  type TeamFormField = 'teamName' | 'teamColor' | 'teamDescription';
  const {
    fieldErrors: teamFieldErrors,
    setFieldErrors: setTeamFieldErrors,
    resetFieldErrors: resetTeamFieldErrors,
  } = useFieldErrors<TeamFormField>();

  const actions = useProgramBillingActions({
    program: currentProgram,
    onProgramChange: setCurrentProgram,
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSaving(false);
    setStep(isUnlimitedTeamCreator() || initialPlanKey ? 'team' : 'plan');
    setCheckoutSecret(null);
    setBillNote('');
    setCheckoutMode('payment');
    setPendingSubscriptionId(null);
    setSelectedKey(initialPlanKey || 'program');
    setIntervals({
      small_team: 'month',
      program: 'month',
      academic: 'year',
      ...(initialPlanKey && initialPlanKey !== 'academic' && initialInterval
        ? { [initialPlanKey]: initialInterval }
        : {}),
    });
    setTeamName('');
    setTeamColor(DEFAULT_COLOR);
    setTeamDescription('');
    resetTeamFieldErrors();
    Promise.all([
      programsService.getBillingPlans(),
      programsService.listPrograms(),
      teamsService.getTeams().catch(() => null),
    ])
      .then(([plansResponse, programResponse, teamsResponse]) => {
        const { plans: planCards, ...planOffer } = plansResponse.data.data;
        setPlans(planCards);
        setOffer(planOffer);
        const openPrograms = programResponse.data.data || [];
        const incomplete = openPrograms.filter((program) => program.status === 'incomplete');
        const keptPrograms = openPrograms.filter((program) => program.status !== 'incomplete');
        setOwnedPrograms(keptPrograms);
        const cycle = accountBillingInterval(keptPrograms);
        if (cycle) {
          setIntervals({ small_team: cycle, program: cycle, academic: 'year' });
        }
        setCurrentProgram(null);
        const teams = (
          (teamsResponse?.data?.data || []) as Array<{
            id: number;
            name: string;
            plan?: string;
            status?: string;
            athlete_count?: number;
          }>
        ).filter((team) => team.plan === 'unlimited' && team.status !== 'cancelled' && team.status !== 'canceled');
        setUnlimitedTeams(teams.map(({ id, name, athlete_count }) => ({ id, name, athlete_count })));
        // An unpaid signup is not a plan. Drop it so the coach can pick again.
        if (incomplete.length > 0) {
          return Promise.all(
            incomplete.map((program) => programsService.cancelProgram(program.id).catch(() => undefined))
          );
        }
        return undefined;
      })
      .catch(() => {
        showSnackbar('Unable to load plans. Please try again later', 'error');
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open]);

  const legacyUnlimitedOnly = unlimitedTeams.length > 0 && ownedPrograms.length === 0;
  const ownerUnlimitedCreate = isUnlimitedTeamCreator();
  const offerDays = offer?.trialDays ?? 0;
  const hasTrialOffer = offerDays > 0;
  const slotsTotal = offer?.earlyAccessSlots ?? 30;
  const slotsRemaining = offer?.earlyAccessSlotsRemaining ?? slotsTotal;
  const slotsUsed = Math.max(slotsTotal - slotsRemaining, 0);
  const trialLabel = !hasTrialOffer
    ? null
    : isEarlyAccessTrial(offer?.trialTier)
    ? '6 month free trial'
    : '1 month free trial';

  const selectedPlan = useMemo(
    () => (selectedKey ? plans.find((plan) => plan.key === selectedKey) || null : null),
    [plans, selectedKey]
  );
  const academicTaken = ownedPrograms.some(
    (program) => program.planKey === 'academic' || program.planKey === 'academic_school'
  );
  const catalogPrograms = ownedPrograms.filter(
    (program) => program.planKey === 'small_team' || program.planKey === 'program'
  );
  const catalogHasMonth = catalogPrograms.some((program) => program.billingInterval === 'month');
  const lockedInterval = accountBillingInterval(ownedPrograms);
  const selectedInterval: BillingInterval =
    selectedKey === 'academic' ? 'year' : lockedInterval || intervals[selectedKey || 'program'] || 'month';
  const selectedPrice = selectedPlan
    ? selectedInterval === 'year'
      ? selectedPlan.yearlyPrice
      : selectedPlan.monthlyPrice
    : null;
  const selectedSuffix = selectedInterval === 'year' ? '/ year' : '/ month';
  const academicUnavailableReason = academicTaken
    ? 'You already have an Academic team. Contact CoeLab if you need another.'
    : null;
  const nextTotals = subscriptionTotals(ownedPrograms);
  if (selectedPrice != null) {
    if (selectedInterval === 'year') nextTotals.year += selectedPrice;
    else nextTotals.month += selectedPrice;
  }

  const programLocked = isProgramLocked(currentProgram);

  const programAtTeamCap = useMemo(() => {
    if (!currentProgram || currentProgram.includedTeams === null) return false;
    if (
      currentProgram.planKey === 'small_team' ||
      currentProgram.planKey === 'program' ||
      currentProgram.planKey === 'academic' ||
      currentProgram.planKey === 'academic_school'
    ) {
      return false;
    }
    return currentProgram.teamCount >= currentProgram.includedTeams;
  }, [currentProgram]);

  const memberCap = currentProgram?.membersPerTeam || selectedPlan?.membersPerTeam || 50;
  const startLabel = hasTrialOffer ? 'Start free trial' : 'Continue to payment';

  const heroTitle =
    step === 'payment'
      ? 'Pay to create your team'
      : ownerUnlimitedCreate
      ? 'Add a squad'
      : legacyUnlimitedOnly
      ? 'CoeLab Unlimited (legacy)'
      : ownedPrograms.length
      ? 'Add a team'
      : 'Create a team';

  const heroSubtitle = (() => {
    if (ownerUnlimitedCreate) {
      return 'Add a team on your founding Unlimited plan. Members are included.';
    }
    if (legacyUnlimitedOnly) {
      return 'New Unlimited teams are closed. Your founding plan stays as-is.';
    }
    if (step === 'payment' || currentProgram?.status === 'incomplete') {
      return 'Your team is created after this payment and then shows up in My Teams.';
    }
    if (ownedPrograms.length) {
      return 'This team joins your monthly or yearly bill. Academic is yearly and still requires a .edu email.';
    }
    if (currentProgram) {
      return `Add a team to your ${currentProgram.planName} plan. Each team holds ${memberCapPhrase(memberCap)}.`;
    }
    if (!hasTrialOffer) {
      return 'You already used your free trial. The plan starts after this payment.';
    }
    return 'Every feature on every plan. This team joins your monthly or yearly bill.';
  })();

  const strip: Array<[string, string]> = hasTrialOffer
    ? [
        ['Every feature', ', day one'],
        ['No card', ' to start'],
        ['Cancel', ' any time'],
      ]
    : [
        ['Every feature', ', day one'],
        ['Cancel', ' any time'],
      ];

  const footNote = (() => {
    if (step === 'payment' || currentProgram?.status === 'incomplete') {
      return (
        <span>
          <b>Payment required.</b> Your team shows up in My Teams after the card payment succeeds.
        </span>
      );
    }
    if (ownerUnlimitedCreate) {
      return <span>This squad stays on CoeLab Unlimited. There is no seat charge.</span>;
    }
    if (legacyUnlimitedOnly) {
      return <span>Moving to Small Team, Large Team, or Academic is case-by-case with CoeLab.</span>;
    }
    if (ownedPrograms.length > 0 && selectedPrice != null) {
      const cycle = selectedInterval === 'year' ? 'yearly' : 'monthly';
      const renewal = ownedPrograms.find((program) => program.billingInterval === selectedInterval)?.currentPeriodEnd;
      return (
        <span>
          <b>
            This team: {formatMoney(selectedPrice)}
            {selectedSuffix}.
          </b>{' '}
          It is added to your {cycle} bill
          {renewal ? `, which renews ${dayjs(renewal).format('MMM D')}` : ''}. You will be paying{' '}
          {formatIntervalTotals(nextTotals)} after that.
          {selectedKey === 'academic' && catalogHasMonth ? ' Your monthly teams move onto this yearly bill.' : ''}
        </span>
      );
    }
    if (currentProgram) {
      if (programLocked) {
        return (
          <span>
            <b>Your teams are locked</b> until payment goes through on Billing.
          </span>
        );
      }
      if (currentProgram.cancelAtPeriodEnd && currentProgram.currentPeriodEnd) {
        return (
          <span>
            <b>Cancellation scheduled</b> for {dayjs(currentProgram.currentPeriodEnd).format('MMM D, YYYY')}. You can
            still add squads until then.
          </span>
        );
      }
      if (
        (currentProgram.planKey === 'small_team' ||
          currentProgram.planKey === 'program' ||
          currentProgram.planKey === 'academic') &&
        currentProgram.includedTeams != null &&
        currentProgram.teamCount >= currentProgram.includedTeams &&
        currentProgram.extraTeamPrice != null
      ) {
        return (
          <span>
            <b>Extra squad.</b>{' '}
            {currentProgram.status === 'trialing'
              ? `Free during your trial, then ${formatMoney(currentProgram.extraTeamPrice)} each.`
              : `${formatMoney(currentProgram.extraTeamPrice)} / ${currentProgram.billingInterval}, prorated now.`}
          </span>
        );
      }
      return (
        <span>
          <b>Included in your plan.</b> No extra charge for this team.
        </span>
      );
    }
    if (!hasTrialOffer) {
      return (
        <span>
          <b>No plan yet.</b> This subscription starts only after the card payment succeeds.
        </span>
      );
    }
    return (
      <span>
        <b>
          {slotsUsed}/{slotsTotal} spots
        </b>{' '}
        for the 6 month free trial
      </span>
    );
  })();

  const teamPayload = () => ({
    teamName: teamName.trim(),
    teamColor,
    teamDescription: teamDescription.trim(),
  });

  const openPaidTeam = (teamId: number) => {
    setPendingSubscriptionId(null);
    clearPendingPaidTeam();
    navigate(`/create-team/success?team_id=${teamId}`);
  };

  const finishCardSetup = (paymentMethodId: string) => {
    if (!selectedKey) return Promise.reject(new Error('Missing plan'));
    setSaving(true);
    return programsService
      .startPaidSeason({
        planKey: selectedKey,
        billingInterval: selectedInterval,
        paymentMethodId,
        ...teamPayload(),
      })
      .then((response) => {
        const data = response.data.data;
        if (data.pending && data.clientSecret && data.subscriptionId) {
          setPendingSubscriptionId(data.subscriptionId);
          savePendingPaidTeam({
            subscriptionId: data.subscriptionId,
            planKey: selectedKey,
            billingInterval: selectedInterval,
            ...teamPayload(),
          });
          setCheckoutMode('payment');
          setCheckoutSecret(data.clientSecret);
          setSaving(false);
          return;
        }
        if (!data.teamId) throw new Error('Missing team');
        openPaidTeam(data.teamId);
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        setSaving(false);
        showSnackbar(error.response?.data?.message || 'Unable to start this plan. Please try again later', 'error');
        return Promise.reject(error);
      });
  };

  const finishInvoiceAuth = () => {
    if (!pendingSubscriptionId || !selectedKey) return Promise.reject(new Error('Missing payment'));
    setSaving(true);
    return programsService
      .finalizePaidSeason({
        subscriptionId: pendingSubscriptionId,
        planKey: selectedKey,
        billingInterval: selectedInterval,
        ...teamPayload(),
      })
      .then((response) => {
        const teamId = response.data.data.teamId;
        if (!teamId) throw new Error('Missing team');
        openPaidTeam(teamId);
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        setSaving(false);
        showSnackbar(error.response?.data?.message || 'Unable to start this plan. Please try again later', 'error');
        return Promise.reject(error);
      });
  };

  const handleClose = () => {
    if (saving) return;
    const subscriptionId = pendingSubscriptionId;
    const unpaidProgramId = currentProgram?.status === 'incomplete' ? currentProgram.id : null;
    if (subscriptionId) {
      programsService.abandonPaidCheckout(subscriptionId).catch(() => undefined);
    }
    if (unpaidProgramId) {
      programsService.cancelProgram(unpaidProgramId).catch(() => undefined);
    }
    if (subscriptionId || checkoutMode === 'setup') {
      clearPendingPaidTeam();
    }
    onClose();
  };

  const finishPaidTeam = (paymentIntentId: string) => {
    if (!currentProgram) return Promise.reject(new Error('Missing program'));
    const programId = currentProgram.id;
    return programsService.confirmPayment(paymentIntentId).then(() => {
      if (currentProgram.teamCount > 0 && currentProgram.teams[0]) {
        clearPendingPaidTeam();
        navigate(`/create-team/success?team_id=${currentProgram.teams[0].id}`);
        return;
      }
      return programsService.addTeam(programId, teamPayload()).then((added) => {
        clearPendingPaidTeam();
        navigate(`/create-team/success?team_id=${added.data.data.teamId}`);
      });
    });
  };

  const handleSubmit = () => {
    resetTeamFieldErrors();
    if (selectedKey === 'academic' && !isAcademicEmail()) {
      showSnackbar('Academic requires an email address that ends with .edu.', 'error');
      return;
    }
    if (selectedKey === 'academic' && academicTaken) {
      showSnackbar('You already have an Academic team. Contact CoeLab if you need another.', 'error');
      return;
    }

    const errors: FieldErrors<TeamFormField> = {};
    if (teamName.trim().length < 1) errors.teamName = 'Team name is required';
    if (teamColor.trim().length < 1) errors.teamColor = 'A team color is required';
    if (teamDescription.trim().length < 1) errors.teamDescription = 'Description is required';
    if (Object.keys(errors).length > 0) {
      setTeamFieldErrors(errors);
      return;
    }

    setSaving(true);
    const payload = teamPayload();
    if (ownerUnlimitedCreate) {
      teamsService
        .createUnlimitedTeam(payload)
        .then((response) => {
          showSnackbar('Team added to your Unlimited plan', 'success');
          navigate(`/create-team/success?team_id=${response.data.data.teamId}`);
        })
        .catch((error: { response?: { data?: { message?: string } } }) => {
          showSnackbar(error.response?.data?.message || 'Unable to create team. Please try again later', 'error');
          setSaving(false);
        });
      return;
    }
    if (!currentProgram && !hasTrialOffer) {
      if (!selectedKey) {
        setSaving(false);
        return;
      }
      savePendingPaidTeam({
        planKey: selectedKey,
        billingInterval: selectedInterval,
        ...payload,
      });
      programsService
        .preparePaidSetup({ planKey: selectedKey, billingInterval: selectedInterval })
        .then((response) => {
          const setup = response.data.data;
          setCheckoutMode('setup');
          setCheckoutSecret(setup.clientSecret);
          const aligned = setup.alignedPlanNames || [];
          if (aligned.length > 0) {
            setBillNote(
              `This also moves ${aligned.join(
                ', '
              )} onto your yearly bill. The charge today is the yearly price for each team, minus unused days on the monthly bill.`
            );
          } else if (setup.joinsExistingBill) {
            const cycle = selectedInterval === 'year' ? 'yearly' : 'monthly';
            const when = setup.renewsAt ? dayjs(setup.renewsAt).format('MMM D') : 'the current renewal date';
            const due =
              setup.estimatedAmountDueCents != null
                ? ` The charge today is ${formatCents(setup.estimatedAmountDueCents)}.`
                : '';
            setBillNote(
              `This team joins your ${cycle} bill, which renews on ${when}.${due} The charge today covers the days left in this period.`
            );
          } else {
            setBillNote('');
          }
          setStep('payment');
          setSaving(false);
        })
        .catch((error: { response?: { data?: { message?: string } } }) => {
          showSnackbar(error.response?.data?.message || 'Unable to start payment. Please try again later', 'error');
          setSaving(false);
        });
      return;
    }
    const request = currentProgram
      ? programsService.addTeam(currentProgram.id, payload)
      : programsService.startSeason({
          planKey: selectedKey as BillingPlanKey,
          billingInterval: selectedInterval,
          ...payload,
        });

    request
      .then((response) => {
        const nextProgram = response.data.data.program;
        const createdTeamId = response.data.data.teamId;
        if (!currentProgram && nextProgram.status === 'incomplete') {
          showSnackbar('Payment is required before this plan starts.', 'error');
          setSaving(false);
          return;
        }
        if (!createdTeamId) {
          showSnackbar('Unable to create team. Please try again later', 'error');
          setSaving(false);
          return;
        }
        showSnackbar(
          currentProgram
            ? 'Team added to your program'
            : `Your ${trialLengthPhrase(nextProgram.trialDays)} free trial has started`,
          'success'
        );
        navigate(`/create-team/success?team_id=${createdTeamId}`);
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to create team. Please try again later', 'error');
        setSaving(false);
      });
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="ctd-loading">
          <CircularProgress size={48} />
        </div>
      );
    }

    if (legacyUnlimitedOnly && !ownerUnlimitedCreate) {
      return (
        <div className="ctd-panel">
          <div className="ctd-lbl">Your Unlimited teams</div>
          <ul className="ctd-legacy-list">
            {unlimitedTeams.map((team) => (
              <li key={team.id}>
                {team.name}
                {team.athlete_count != null ? <span> · {team.athlete_count} athletes</span> : null}
              </li>
            ))}
          </ul>
          <p className="ctd-copy">
            Add athletes or cancel from Manage Team on an existing squad. New Unlimited teams can’t be created.
          </p>
        </div>
      );
    }

    if (step === 'plan' && !currentProgram && !ownerUnlimitedCreate) {
      return (
        <>
          <div className="ctd-lbl">Choose a plan</div>
          <PlanPicker
            plans={plans}
            selectedKey={selectedKey}
            intervals={intervals}
            trialLabel={ownedPrograms.length ? null : trialLabel}
            academicUnavailableReason={academicUnavailableReason}
            lockedInterval={lockedInterval}
            onSelect={setSelectedKey}
            onIntervalChange={(key, interval) => setIntervals((current) => ({ ...current, [key]: interval }))}
          />
          <PlanIncludes />
        </>
      );
    }

    if (step === 'payment') {
      return (
        <div className="ctd-panel">
          <div className="ctd-lbl">Card details</div>
          <p className="ctd-copy">{billNote || 'Your plan and team are created when this payment succeeds.'}</p>
          {checkoutSecret && stripePromise ? (
            <Elements
              key={checkoutSecret}
              stripe={stripePromise}
              options={{ clientSecret: checkoutSecret, appearance: { theme: 'stripe' } }}
            >
              <ProgramCheckoutForm
                buttonLabel="Pay and create team"
                onSetup={checkoutMode === 'setup' ? finishCardSetup : undefined}
                onPaid={pendingSubscriptionId ? () => finishInvoiceAuth() : finishPaidTeam}
              />
            </Elements>
          ) : (
            <div className="ctd-loading">
              <CircularProgress size={36} />
            </div>
          )}
        </div>
      );
    }

    if (programLocked && currentProgram && currentProgram.status !== 'incomplete') {
      return (
        <div className="ctd-panel">
          <div className="ctd-lbl">Payment needed first</div>
          <p className="ctd-copy">
            Your free trial has ended. Convert to paid on Billing to keep your teams and add squads.
          </p>
        </div>
      );
    }

    if (programAtTeamCap && currentProgram) {
      return (
        <div className="ctd-panel">
          <div className="ctd-lbl">Team limit reached</div>
          <p className="ctd-copy">
            {currentProgram.planKey === 'small_team'
              ? 'Small Team includes 1 squad of 10 members. Another squad is the same price as the plan.'
              : `Your plan includes ${currentProgram.includedTeams} team. Upgrade on Billing if you need another squad.`}
          </p>
        </div>
      );
    }

    return (
      <div className="ctd-team">
        <div className="ctd-panel">
          <div className="ctd-lbl">Team details</div>
          <TextField
            id="create-team-field-teamName"
            required
            label="Team name"
            value={teamName}
            fullWidth
            size="small"
            margin="dense"
            autoFocus
            error={!!teamFieldErrors.teamName}
            helperText={teamFieldErrors.teamName || ''}
            onChange={(event) => {
              setTeamName(event.target.value);
              if (teamFieldErrors.teamName)
                setTeamFieldErrors((p) => {
                  const n = { ...p };
                  delete n.teamName;
                  return n;
                });
            }}
          />
          <MuiColorInput
            value={teamColor}
            format="hex"
            isAlphaHidden
            size="small"
            label="Team color"
            style={{ width: '100%', marginTop: 12 }}
            onChange={(color: string) => {
              setTeamColor(color);
              if (teamFieldErrors.teamColor)
                setTeamFieldErrors((p) => {
                  const n = { ...p };
                  delete n.teamColor;
                  return n;
                });
            }}
          />
          {teamFieldErrors.teamColor && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {teamFieldErrors.teamColor}
            </Typography>
          )}
          <TextField
            id="create-team-field-teamDescription"
            required
            label="Description"
            value={teamDescription}
            fullWidth
            size="small"
            margin="normal"
            multiline
            minRows={3}
            inputProps={{ maxLength: 200 }}
            error={!!teamFieldErrors.teamDescription}
            helperText={teamFieldErrors.teamDescription || `${teamDescription.length}/200`}
            onChange={(event) => {
              setTeamDescription(event.target.value);
              if (teamFieldErrors.teamDescription)
                setTeamFieldErrors((p) => {
                  const n = { ...p };
                  delete n.teamDescription;
                  return n;
                });
            }}
          />
        </div>

        <div className="ctd-panel ctd-summary">
          <div className="ctd-lbl">{currentProgram || ownerUnlimitedCreate ? 'Your plan' : 'Selected plan'}</div>
          <div className="ctd-summary-name">
            {ownerUnlimitedCreate ? 'CoeLab Unlimited' : currentProgram ? currentProgram.planName : selectedPlan?.name}
          </div>
          {!currentProgram && !ownerUnlimitedCreate && selectedPrice != null ? (
            <div className="ctd-summary-price">
              <b>{formatMoney(selectedPrice)}</b>
              <span>{selectedSuffix}</span>
            </div>
          ) : null}
          <ul className="ctd-summary-list">
            <li>
              {ownerUnlimitedCreate
                ? 'Members are included on this team'
                : `Each team holds ${memberCapPhrase(memberCap)}`}
            </li>
            {ownerUnlimitedCreate ? (
              <li>Founding Unlimited · no seat charge</li>
            ) : currentProgram ? (
              <li>
                {currentProgram.teamCount} {currentProgram.teamCount === 1 ? 'team' : 'teams'} on this plan
                {currentProgram.includedTeams != null ? ` · ${currentProgram.includedTeams} included` : ''}
              </li>
            ) : hasTrialOffer ? (
              <li>Free for {trialLengthPhrase(offerDays)} — no card until you convert</li>
            ) : ownedPrograms.length ? (
              <li>Added to your bill · you will be paying {formatIntervalTotals(nextTotals)}</li>
            ) : (
              <li>The plan starts after this payment</li>
            )}
            <li>Every feature on every plan</li>
          </ul>
          {!currentProgram && !ownerUnlimitedCreate ? (
            <button type="button" className="ctd-link" onClick={() => setStep('plan')}>
              Change plan
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderFooterAction = () => {
    if (loading) return null;
    if (legacyUnlimitedOnly && !ownerUnlimitedCreate) {
      return (
        <>
          <button type="button" className="ctd-secondary" onClick={onClose}>
            Back to My Teams
          </button>
          <button type="button" className="ctd-go" onClick={() => navigate('/billing')}>
            View plan on Billing
            <Arrow />
          </button>
        </>
      );
    }
    if (step === 'plan' && !currentProgram && !ownerUnlimitedCreate) {
      return (
        <button
          type="button"
          className="ctd-go"
          onClick={() => {
            if (!selectedKey) {
              showSnackbar('Choose a plan to continue', 'error');
              return;
            }
            if (selectedKey === 'academic' && !isAcademicEmail()) {
              showSnackbar('Academic requires an email address that ends with .edu.', 'error');
              return;
            }
            if (selectedKey === 'academic' && academicTaken) {
              showSnackbar('You already have an Academic team. Contact CoeLab if you need another.', 'error');
              return;
            }
            setStep('team');
          }}
        >
          Continue
          <Arrow />
        </button>
      );
    }
    if (step === 'payment') {
      return currentProgram && currentProgram.teamCount === 0 ? (
        <button type="button" className="ctd-secondary" onClick={() => setStep('team')} disabled={saving}>
          Back
        </button>
      ) : null;
    }
    if (programLocked && currentProgram?.status !== 'incomplete') {
      return (
        <button type="button" className="ctd-go" onClick={() => navigate('/billing')}>
          Go to Billing
          <Arrow />
        </button>
      );
    }
    if (programAtTeamCap && currentProgram) {
      return currentProgram.planKey === 'small_team' ? (
        <>
          <button type="button" className="ctd-secondary" onClick={() => navigate('/billing')}>
            View billing
          </button>
          <button
            type="button"
            className="ctd-go"
            disabled={actions.planChangeLoading}
            onClick={() => actions.requestChangePlan('program', currentProgram.billingInterval)}
          >
            Upgrade to Large Team
            <Arrow />
          </button>
        </>
      ) : (
        <button type="button" className="ctd-go" onClick={() => navigate('/billing')}>
          View billing
          <Arrow />
        </button>
      );
    }
    return (
      <>
        {!currentProgram && !ownerUnlimitedCreate ? (
          <button type="button" className="ctd-secondary" onClick={() => setStep('plan')} disabled={saving}>
            Back
          </button>
        ) : null}
        <button type="button" className="ctd-go" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : currentProgram?.status === 'incomplete' ? (
            'Continue to payment'
          ) : currentProgram || ownerUnlimitedCreate ? (
            'Add team'
          ) : (
            startLabel
          )}
          {saving ? null : <Arrow />}
        </button>
      </>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : handleClose}
        fullScreen={fullScreen}
        maxWidth={false}
        PaperProps={{
          className: `ctd-paper${
            step === 'team' || step === 'payment' || legacyUnlimitedOnly || ownerUnlimitedCreate
              ? ' ctd-paper-narrow'
              : ''
          }`,
        }}
      >
        <div className="ctd-hero">
          <div className="ctd-head">
            <span className="ctd-tile">
              <GroupsOutlinedIcon />
            </span>
            <div>
              <h2>{heroTitle}</h2>
              <p>{heroSubtitle}</p>
            </div>
          </div>
          {!currentProgram && !legacyUnlimitedOnly && !ownerUnlimitedCreate ? (
            <div className="ctd-strip">
              {strip.map(([bold, rest]) => (
                <div className="ctd-strip-item" key={bold}>
                  <span>
                    <Check />
                  </span>
                  <div>
                    <b>{bold}</b>
                    {rest}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="ctd-strip-spacer" />
          )}
          <button type="button" className="ctd-x" aria-label="Close" onClick={handleClose} disabled={saving}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="ctd-body">{renderBody()}</div>

        {!loading ? (
          <div className="ctd-foot">
            <div
              className={`ctd-free${
                legacyUnlimitedOnly || currentProgram || ownerUnlimitedCreate ? ' ctd-free-neutral' : ''
              }`}
            >
              <i />
              {footNote}
            </div>
            <div className="ctd-foot-actions">{renderFooterAction()}</div>
          </div>
        ) : null}
      </Dialog>
      {actions.dialogs}
    </>
  );
};

export default CreateTeamDialog;
